import React, { useState } from 'react';
import { X, Calendar, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { Button } from './ui/button';

interface JumpToDateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectDate: (date: Date) => void;
  entriesMap?: Map<string, number>; // Map of date strings to entry count
  initialDate?: Date;
}

export default function JumpToDateModal({
  isOpen,
  onClose,
  onSelectDate,
  entriesMap,
  initialDate = new Date(),
}: JumpToDateModalProps) {
  const [currentMonth, setCurrentMonth] = useState(
    new Date(initialDate.getFullYear(), initialDate.getMonth(), 1)
  );
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  if (!isOpen) return null;

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const daysInMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() + 1,
    0
  ).getDate();

  const firstDayOfMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
    1
  ).getDay();

  const previousMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  };

  const nextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
  };

  const handleDateClick = (day: number) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    setSelectedDate(date);
  };

  const handleConfirm = () => {
    if (selectedDate) {
      onSelectDate(selectedDate);
      onClose();
    }
  };

  const handleToday = () => {
    const today = new Date();
    setSelectedDate(today);
    setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  const getDateKey = (day: number) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return date.toISOString().split('T')[0];
  };

  const hasEntries = (day: number) => {
    if (!entriesMap) return false;
    const dateKey = getDateKey(day);
    return entriesMap.has(dateKey) && (entriesMap.get(dateKey) || 0) > 0;
  };

  const isToday = (day: number) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentMonth.getMonth() === today.getMonth() &&
      currentMonth.getFullYear() === today.getFullYear()
    );
  };

  const isSelected = (day: number) => {
    if (!selectedDate) return false;
    return (
      day === selectedDate.getDate() &&
      currentMonth.getMonth() === selectedDate.getMonth() &&
      currentMonth.getFullYear() === selectedDate.getFullYear()
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center md:justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white w-full md:max-w-md md:rounded-2xl rounded-t-3xl shadow-2xl flex flex-col max-h-[85vh] md:max-h-[600px] animate-slide-up">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-t-3xl md:rounded-t-2xl flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <Calendar className="h-6 w-6" />
              <h2 className="text-xl font-bold">Jump to Date</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/20 active:bg-white/30 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-sm text-blue-100">
            Select a date to view entries from that day
          </p>
        </div>

        {/* Calendar Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={previousMonth}
              className="p-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors"
            >
              <ChevronLeft className="h-5 w-5 text-gray-700" />
            </button>
            <div className="text-center">
              <h3 className="font-bold text-gray-900">
                {monthNames[currentMonth.getMonth()]}
              </h3>
              <p className="text-sm text-gray-600">{currentMonth.getFullYear()}</p>
            </div>
            <button
              onClick={nextMonth}
              className="p-2 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors"
            >
              <ChevronRight className="h-5 w-5 text-gray-700" />
            </button>
          </div>

          {/* Quick Actions */}
          <div className="mb-4">
            <Button
              onClick={handleToday}
              variant="outline"
              className="w-full h-10"
            >
              <Calendar className="h-4 w-4 mr-2" />
              Jump to Today
            </Button>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div
                key={day}
                className="text-center text-xs font-semibold text-gray-600 py-2"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {/* Empty cells for days before month starts */}
            {Array.from({ length: firstDayOfMonth }).map((_, index) => (
              <div key={`empty-${index}`} className="aspect-square" />
            ))}

            {/* Days of the month */}
            {Array.from({ length: daysInMonth }).map((_, index) => {
              const day = index + 1;
              const hasEntry = hasEntries(day);
              const isTodayDate = isToday(day);
              const isSelectedDate = isSelected(day);

              return (
                <button
                  key={day}
                  onClick={() => handleDateClick(day)}
                  className={`
                    aspect-square rounded-lg flex flex-col items-center justify-center
                    text-sm font-medium transition-all relative
                    ${isSelectedDate
                      ? 'bg-blue-600 text-white shadow-lg scale-105'
                      : isTodayDate
                      ? 'bg-blue-100 text-blue-900 border-2 border-blue-600'
                      : hasEntry
                      ? 'bg-green-50 text-green-900 hover:bg-green-100 active:bg-green-200'
                      : 'text-gray-700 hover:bg-gray-100 active:bg-gray-200'
                    }
                  `}
                >
                  <span className="relative z-10">{day}</span>
                  
                  {/* Entry Indicator Dot */}
                  {hasEntry && !isSelectedDate && (
                    <div className="absolute bottom-1 w-1 h-1 bg-green-600 rounded-full" />
                  )}

                  {/* Selected Check */}
                  {isSelectedDate && (
                    <div className="absolute top-0.5 right-0.5">
                      <Check className="h-3 w-3" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="mt-4 p-3 bg-gray-50 rounded-lg space-y-2">
            <p className="text-xs font-semibold text-gray-700 mb-2">Legend:</p>
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 rounded bg-green-50 border border-green-200 flex items-center justify-center">
                <div className="w-1 h-1 bg-green-600 rounded-full" />
              </div>
              <span className="text-xs text-gray-600">Has entries</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 rounded bg-blue-100 border-2 border-blue-600" />
              <span className="text-xs text-gray-600">Today</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-6 h-6 rounded bg-blue-600" />
              <span className="text-xs text-gray-600">Selected</span>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-white border-t p-4 flex space-x-3 flex-shrink-0">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 h-12"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedDate}
            className="flex-1 h-12 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Check className="h-5 w-5 mr-2" />
            Jump to Date
          </Button>
        </div>
      </div>
    </div>
  );
}
